/** Visual phrase comparison only: it never grades an answer or decides which alternatives pass. */
export interface ComparedPhrase {
  given: string;
  expected: string;
  unchanged: boolean;
}

const wordKey = (word: string) => word.normalize('NFC').toLowerCase().replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, '');

/** Attach standalone punctuation to a neighbouring word so it never becomes a fake word error. */
function wordsOf(text: string): string[] {
  const words: string[] = [];
  let prefix = '';
  for (const token of text.trim().split(/\s+/u).filter(Boolean)) {
    if (wordKey(token)) {
      words.push(prefix + token);
      prefix = '';
    } else if (words.length) {
      words[words.length - 1] += token;
    } else {
      prefix += token;
    }
  }
  return words;
}

/** Keep shared words as anchors and group adjacent differences, without artificial word pairs. */
export function compareAnswerPhrases(given: string, expected: string): { parts: ComparedPhrase[]; orderChanged: boolean } {
  const before = wordsOf(given);
  const after = wordsOf(expected);
  if (!before.length || !after.length) return { parts: [{ given, expected, unchanged: true }], orderChanged: false };
  // An arbitrarily long pasted answer must not allocate a quadratic table on the phone.
  if (before.length > 100 || after.length > 100) return { parts: [{ given, expected, unchanged: false }], orderChanged: false };
  const beforeKeys = before.map(wordKey);
  const afterKeys = after.map(wordKey);
  const orderChanged = beforeKeys.length === afterKeys.length
    && beforeKeys.slice().sort().join('\u0001') === afterKeys.slice().sort().join('\u0001')
    && beforeKeys.join('\u0001') !== afterKeys.join('\u0001');
  if (orderChanged) return { parts: [{ given, expected, unchanged: false }], orderChanged: true };
  const shared = Array.from({ length: before.length + 1 }, () => new Uint16Array(after.length + 1));
  for (let i = before.length - 1; i >= 0; i--) {
    for (let j = after.length - 1; j >= 0; j--) {
      shared[i][j] = beforeKeys[i] === afterKeys[j]
        ? 1 + shared[i + 1][j + 1]
        : Math.max(shared[i + 1][j], shared[i][j + 1]);
    }
  }

  const out: ComparedPhrase[] = [];
  let i = 0;
  let j = 0;
  let removed: string[] = [];
  let added: string[] = [];
  const flush = () => {
    if (removed.length || added.length) {
      out.push({ given: removed.join(' '), expected: added.join(' '), unchanged: false });
    }
    removed = [];
    added = [];
  };
  while (i < before.length || j < after.length) {
    if (i < before.length && j < after.length && beforeKeys[i] === afterKeys[j]) {
      flush();
      out.push({ given: before[i++], expected: after[j++], unchanged: true });
    } else if (i < before.length && (j === after.length || shared[i + 1][j] >= shared[i][j + 1])) {
      removed.push(before[i++]);
    } else {
      added.push(after[j++]);
    }
  }
  flush();
  return { parts: out, orderChanged: false };
}
